import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useDisplayPrice } from "@majourneys/currency";
import type { AiPlaceLayout } from "@majourneys/shared";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { panelStyle } from "../../../shared/ui/card-frame";
import type { ChatLang } from "../hooks/use-ai-chat";
import {
  actionLabelKey,
  FreeToReserve,
  isRestaurantRef,
  LayoutButton,
  LayoutLabel,
  placeHref,
} from "./layout-parts";
import { SOFT } from "./soft-tokens";
import { usePriceUnit } from "../lib/price-unit";
import { isStayItem } from "@majourneys/shared";

const FRAME_RADIUS = 20;
const FRAME_PAD = 6;

/**
 * The One-place answer: a single card for the venue the traveller named, in
 * one fixed reading order (owner, 2026-09-24, after the first version put a
 * five-line paragraph, a repeated bullet and three loose advice lines above a
 * very tall photo):
 *
 *   photo (16:9, never taller than 260px) → name and what/where →
 *   fact chips (hours first, pay on arrival where true) → OUR PICK →
 *   WORTH KNOWING → START HERE → QUESTIONS → price and the button.
 *
 * The prose above the card is only its lead. Chat kit C19 (a bookable
 * place) and C24 (an explore place) share this card:
 *
 *  - an activity shows "From" its price, due on the day, never charged here;
 *  - a restaurant says "Free to reserve" and never a price;
 *  - an explore place shows no price, no booking wording and no pay chip,
 *    just "Visit on your own" and a quiet "Read more" to its page.
 *
 * There is no "Open now": the layout carries no live opening state, and a
 * badge we cannot keep true is worse than none. Opening hours, when known,
 * arrive as the first fact chip.
 */
export function PlaceView({ layout, lang }: { layout: AiPlaceLayout; lang: ChatLang }) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const priceUnit = usePriceUnit(layout.place);
  const { place, facts, faqs } = layout;
  const href = placeHref(place, lang);
  const meta = [place.categoryName, place.cityName].filter(Boolean).join(" · ");
  const explore = place.mode === "explore";
  const restaurant = !explore && isRestaurantRef(place);
  const showPrice =
    place.mode === "bookable" &&
    !restaurant &&
    place.priceMad != null &&
    place.action !== "book_ride";
  // Never a pay chip on a place we do not sell.
  const chips =
    layout.payOnArrival && !explore ? [...facts, t("ai_concierge.place_pay_on_arrival")] : facts;
  const advice = [
    { key: "pick", label: t("ai_concierge.advice_pick"), text: layout.ourPick },
    { key: "caution", label: t("ai_concierge.advice_caution"), text: layout.worthKnowing },
    { key: "first", label: t("ai_concierge.advice_first_move"), text: layout.startHere },
  ].filter((a): a is { key: string; label: string; text: string } => !!a.text);

  return (
    <article
      style={{ ...panelStyle(FRAME_RADIUS, "#fff"), padding: FRAME_PAD }}
      data-testid={`place-card-${place.slug}`}
    >
      <div
        className="relative aspect-[16/9] max-h-[260px] w-full overflow-hidden"
        style={{ background: "#eceaf5", borderRadius: FRAME_RADIUS - FRAME_PAD }}
        data-testid="place-photo"
      >
        {place.heroImageUrl ? (
          <ImageWithFallback
            src={place.heroImageUrl}
            alt=""
            width={640}
            height={360}
            sizes="(max-width: 640px) 100vw, 560px"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="grid h-full w-full place-items-center">
            <Icon name="ri-image-2-line" size={24} style={{ color: SOFT.body }} aria-hidden="true" />
          </div>
        )}
      </div>

      <div className="px-2.5 pb-2.5 pt-3">
        <h3 className="text-start text-[15px] font-semibold leading-[20px]" style={{ color: SOFT.ink }}>
          {place.title}
        </h3>
        {meta ? (
          <p className="mt-0.5 text-start text-[12px]" style={{ color: SOFT.body }}>
            {meta}
          </p>
        ) : null}
        {explore ? (
          <span
            className="mt-1.5 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{ background: SOFT.tint, color: SOFT.body }}
            data-testid="place-explore-badge"
          >
            {t("ai_concierge.visit_on_your_own", { defaultValue: "Visit on your own" })}
          </span>
        ) : null}

        {chips.length > 0 ? (
          <ul className="mt-3 flex flex-wrap gap-1.5" data-testid="place-facts">
            {chips.map((fact) => (
              <li
                key={fact}
                className="rounded-full px-2.5 py-1 text-[12px] font-medium"
                style={{ background: SOFT.tint, color: SOFT.ink }}
              >
                {fact}
              </li>
            ))}
          </ul>
        ) : null}

        {advice.map((a) => (
          <Section key={a.key} label={a.label} testId={`place-${a.key}`}>
            <p className="text-start text-[14px] leading-[21px]" style={{ color: SOFT.body }}>
              {a.text}
            </p>
          </Section>
        ))}

        {faqs.length > 0 ? (
          <Section label={t("ai_concierge.place_questions")} testId="place-questions">
            <dl className="flex flex-col gap-2.5">
              {faqs.map((faq) => (
                <div key={faq.question}>
                  <dt className="text-start text-[13.5px] font-semibold" style={{ color: SOFT.ink }}>
                    {faq.question}
                  </dt>
                  <dd className="mt-0.5 text-start text-[13px] leading-[18px]" style={{ color: SOFT.body }}>
                    {faq.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </Section>
        ) : null}

        <div
          className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3"
          style={{ borderColor: SOFT.hairline }}
        >
          {showPrice ? (
            <div className="min-w-0">
              <div className="text-[15px] font-semibold leading-none tabular-nums" style={{ color: SOFT.ink }}>
                {t("ai_concierge.place_from", { price: displayPrice(place.priceMad!) })}
                {priceUnit ? (
                  <span className="text-[12px] font-medium" style={{ color: SOFT.body }}>
                    {` ${priceUnit}`}
                  </span>
                ) : null}
              </div>
              {/* The server says whether this place really is paid on the
                  day. A camp is a stay and is paid online; the card said
                  "Total due on the day" for Alkamar Camp (2026-10-30). */}
              <div className="mt-1 text-[11.5px]" style={{ color: SOFT.body }}>
                {layout.payOnArrival
                  ? t("ai_concierge.due_on_the_day")
                  : isStayItem(place.categoryName, place.segment, place.priceType)
                    ? t("stays.receipt.paid_label")
                    : null}
              </div>
            </div>
          ) : restaurant ? (
            <FreeToReserve label={t("ai_concierge.free_to_reserve", { defaultValue: "Free to reserve" })} />
          ) : (
            <span />
          )}
          {explore ? (
            <LayoutButton href={href}>
              {t("ai_concierge.read_more", { defaultValue: "Read more" })}
            </LayoutButton>
          ) : (
            <LayoutButton href={href} primary>
              {t(actionLabelKey(place.action))}
            </LayoutButton>
          )}
        </div>
      </div>
    </article>
  );
}

function Section({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <section className="mt-4" data-testid={testId}>
      <LayoutLabel>{label}</LayoutLabel>
      <div className="mt-1">{children}</div>
    </section>
  );
}
